import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import './Radio.css';

/**
 * Radio · RadioGroup · CardRadio — 디자인 정본 B6.
 *
 * ★ **지금 네이티브 `type="radio"` 는 3회 / 2파일뿐이다** — 나머지는 Select 나 버튼 그룹으로
 *   대체돼 있다. 그러니 이 컴포넌트의 일은 「많은 것을 흡수」가 아니라
 *   **「라디오가 맞는 자리에 라디오를 쓸 수 있게」** 하는 것이다.
 *
 * ★★ **언제 라디오인가** — 선택지가 **한눈에 다 보여야 하고**(대여섯 이하),
 *   고른 것끼리 **견줘 봐야 할 때**. 목록이 길면 `Select`, 켜고 끄는 것이면 `Switch` 다.
 *
 * ★★ 묶음은 `<fieldset>` + `<legend>` 다. 그래야 스크린리더가 **「무엇을 고르는 중인지」**를
 *   각 선택지마다 다시 읽어 준다 — `div` 로 묶으면 「고정 근무제」만 읽히고 그게 무슨 질문의
 *   답인지는 안 읽힌다.
 */

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, className, disabled, ...rest },
  ref,
) {
  return (
    <label className={['wc-radio', disabled ? 'wc-radio--disabled' : '', className ?? ''].filter(Boolean).join(' ')}>
      {/* 체크박스와 같은 규칙 — 지우지 않고 겹쳐 둔다(키보드·스크린리더가 찾아야 한다). */}
      <input ref={ref} type="radio" className="wc-radio__input" disabled={disabled} {...rest} />
      <span className="wc-radio__dot" aria-hidden />
      {label != null && <span className="wc-radio__label">{label}</span>}
    </label>
  );
});

export interface RadioGroupProps {
  /** 무엇을 고르는 중인지. `<legend>` 로 들어간다 — 생략하지 않는다. */
  legend: ReactNode;
  /** 라디오들이 공유하는 name. 안 주면 서로 다른 묶음으로 취급돼 여러 개가 동시에 켜진다. */
  name: string;
  children: ReactNode;
  /** 가로로 늘어놓는다. 선택지가 짧을 때만. */
  inline?: boolean;
  className?: string;
}

export function RadioGroup({ legend, name, children, inline = false, className }: RadioGroupProps) {
  return (
    <fieldset
      className={['wc-radio-group', inline ? 'wc-radio-group--inline' : '', className ?? ''].filter(Boolean).join(' ')}
      data-name={name}
    >
      <legend className="wc-radio-group__legend">{legend}</legend>
      {children}
    </fieldset>
  );
}

/* ⚠ `title` 은 네이티브 속성 이름(문자열 툴팁)이라 함께 뺀다 — 안 빼면 우리 것과 부딪힌다. */
export interface CardRadioProps extends Omit<RadioProps, 'label' | 'title'> {
  title: ReactNode;
  /** 이 선택지가 **무엇을 뜻하는지**. 설명이 없으면 그냥 Radio 를 쓴다. */
  description?: ReactNode;
}

/**
 * CardRadio — 선택지가 **설명을 동반할 때** (정본 B6).
 * 예: 「고정 근무제 — 09:00–18:00 · 지각·조퇴를 판정한다」 vs 「선택 근로제 — 정산 기간 안에서
 * 총 근로시간만 맞추면 된다」. 이런 것을 한 줄 라디오로 늘어놓으면 **뜻을 모르고 고르게 된다.**
 */
export const CardRadio = forwardRef<HTMLInputElement, CardRadioProps>(function CardRadio(
  { title, description, className, disabled, ...rest },
  ref,
) {
  return (
    <label className={['wc-cardradio', disabled ? 'wc-cardradio--disabled' : '', className ?? ''].filter(Boolean).join(' ')}>
      <input ref={ref} type="radio" className="wc-radio__input" disabled={disabled} {...rest} />
      <span className="wc-radio__dot" aria-hidden />
      <span className="wc-cardradio__body">
        <span className="wc-cardradio__title">{title}</span>
        {description != null && <span className="wc-cardradio__desc">{description}</span>}
      </span>
    </label>
  );
});

/**
 * PreviewRadio — 고르면 **화면 모양이 바뀌는** 값 (디자인 정본 B6-2).
 *
 * ★★ 글자만으로는 「좁게 / 넓게」가 안 그려진다. 설명을 두 줄 붙여도 사용자는
 *    **눌러 보고서야** 안다. 68×44 도해가 그 한 번을 없앤다.
 *
 * ★★ **그림은 도해다 — 스크린샷이 아니다.** 실제 화면을 축소해 「찍으면」 그 화면의 CSS 가
 *    바뀔 때마다 그림도 함께 상하고, 결국 아무도 안 고친다. 도해는 **무엇이 보이고
 *    무엇이 안 보이는지**만 말한다.
 *
 * ★ 도해에는 `aria-hidden` 이 붙는다 — 뜻은 제목·설명이 진다. 그림에만 뜻을 싣고 제목을
 *   「A / B」로 두면 스크린리더는 아무것도 못 읽는다.
 * ★ 선택지는 둘~셋. 넷을 넘으면 도해끼리 안 갈린다 → `Select` 를 쓴다.
 */
export interface PreviewRadioProps extends Omit<CardRadioProps, 'children'> {
  /** 68×44 인라인 SVG. 색은 토큰만 쓴다(이미지 파일·이모지 금지). */
  figure: ReactNode;
}

export const PreviewRadio = forwardRef<HTMLInputElement, PreviewRadioProps>(function PreviewRadio(
  { figure, title, description, className, disabled, ...rest },
  ref,
) {
  return (
    <label
      className={['wc-cardradio', 'wc-prvradio', disabled ? 'wc-cardradio--disabled' : '', className ?? '']
        .filter(Boolean).join(' ')}
    >
      <input ref={ref} type="radio" className="wc-radio__input" disabled={disabled} {...rest} />
      <span className="wc-radio__dot" aria-hidden />
      <span className="wc-prvradio__fig" aria-hidden>{figure}</span>
      <span className="wc-cardradio__body">
        <span className="wc-cardradio__title">{title}</span>
        {description != null && <span className="wc-cardradio__desc">{description}</span>}
      </span>
    </label>
  );
});
